/**
 * Centrale JSON-LD bouwstenen voor het verzendbeleid.
 *
 * Beleid (zie SHIPPING_POLICY_TEXT in commerce.ts):
 * - Levering binnen Nederland en België
 * - Verzendkosten worden vóór betaling getoond
 * - Alleen binnen Nederland gratis verzending vanaf €150 (incl. btw)
 */

export const SHIPPING_COUNTRIES = ["NL", "BE"] as const;
export const FREE_SHIPPING_THRESHOLD_INCL_CENTS = 15000;
export const STANDARD_SHIPPING_INCL_CENTS = 695;
/** Verzendkosten België (geen gratis-verzendingsdrempel). */
export const BE_SHIPPING_INCL_CENTS = 1295;

const euro = (cents: number) => (cents / 100).toFixed(2);

function deliveryTime(minTransit = 1, maxTransit = 2) {
  return {
    "@type": "ShippingDeliveryTime",
    handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
    transitTime: { "@type": "QuantitativeValue", minValue: minTransit, maxValue: maxTransit, unitCode: "DAY" },
  };
}

/**
 * OfferShippingDetails voor NL (betaald onder €150, gratis vanaf €150) en BE
 * (altijd betaald). `shippingCents` is het NL-tarief incl. btw voor dit product.
 */
export function offerShippingDetails(shippingInclCents: number) {
  return [
    {
      "@type": "OfferShippingDetails",
      name: "Nederland (onder de gratis verzenddrempel)",
      shippingRate: {
        "@type": "MonetaryAmount",
        value: euro(shippingInclCents),
        currency: "EUR",
      },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "NL" },
      deliveryTime: deliveryTime(),
      eligibleTransactionVolume: {
        "@type": "PriceSpecification",
        priceCurrency: "EUR",
        maxPrice: euro(FREE_SHIPPING_THRESHOLD_INCL_CENTS - 1),
        valueAddedTaxIncluded: true,
      },
    },
    {
      "@type": "OfferShippingDetails",
      name: `Nederland — gratis verzending vanaf €${euro(FREE_SHIPPING_THRESHOLD_INCL_CENTS)}`,
      shippingRate: { "@type": "MonetaryAmount", value: "0.00", currency: "EUR" },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "NL" },
      deliveryTime: deliveryTime(),
      eligibleTransactionVolume: {
        "@type": "PriceSpecification",
        priceCurrency: "EUR",
        minPrice: euro(FREE_SHIPPING_THRESHOLD_INCL_CENTS),
        valueAddedTaxIncluded: true,
      },
    },
    {
      "@type": "OfferShippingDetails",
      name: "België",
      shippingRate: {
        "@type": "MonetaryAmount",
        value: euro(BE_SHIPPING_INCL_CENTS),
        currency: "EUR",
      },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "BE" },
      deliveryTime: deliveryTime(2, 3),
    },
  ];
}

/** Verzendgebied als schema.org Country-lijst. */
export const SHIPPING_AREA_SERVED = [
  { "@type": "Country", name: "Nederland" },
  { "@type": "Country", name: "België" },
];
